import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
// Only the week arithmetic is shared: RecordWeekBin has no series dimension, so the binning is restated here.
import { DAY_MS, toDateString, toUtcTimestamp, WEEK_MS } from './useRecordsAggregation';

/** How many series are checked by default, before a reader opens the dropdown. */
const TOP_DEFAULT_COUNT = 5;

/**
 * One week's value of one series, as a grouped weekly chart reads it: the page maps its own rows
 * (a Feed Mix's delivered quantity, a drug's treatments) onto this shape.
 */
export type GroupedWeeklyRow = {
    /** Monday of the week, `yyyy-MM-dd`, as the server decided it. */
    week_start: string;
    id: number;
    name: string;
    value: number;
};

export type SeriesRanking = {
    id: number;
    name: string;
    total: number;
};

export type GroupedWeekBin = {
    weekStart: string;
    /** UTC midnight of `weekStart`, the x value Highcharts plots. */
    timestamp: number;
    /** Value per series id in this week; a series absent here had nothing. */
    valueById: Map<number, number>;
};

export type GroupedWeeklySeries = {
    id: number;
    name: string;
    data: { x: number; y: number }[];
};

/** The days, `yyyy-MM-dd`, a chart's week axis has to cover whether or not they hold any rows. */
export type WeeklyBinRange = { from: string; to: string };

/** UTC midnight of the Monday on or before a `yyyy-MM-dd` day. */
function mondayOf(day: string): number {
    const timestamp = toUtcTimestamp(day);

    return timestamp - ((new Date(timestamp).getUTCDay() + 6) % 7) * DAY_MS;
}

/**
 * Value per series per week, one bin per week from the first populated week to the last - or, given
 * a range, from the week its first day falls in to the week of its last, so a period's quiet weeks
 * at either end show as empty rather than being cut off.
 *
 * Weeks with no rows still get a bin - every visible series zero-fills through it below - so the
 * bars stay evenly spaced, as they do on the single-series records charts. Each filled week is a
 * whole number of weeks after a `week_start` the server already decided; a range only adds Mondays
 * around them.
 */
export function buildWeeklyBins(rows: GroupedWeeklyRow[], range: WeeklyBinRange | null = null): GroupedWeekBin[] {
    const byWeek = new Map<string, Map<number, number>>();

    for (const row of rows) {
        const byId = byWeek.get(row.week_start) ?? new Map<number, number>();

        byId.set(row.id, (byId.get(row.id) ?? 0) + row.value);
        byWeek.set(row.week_start, byId);
    }

    const timestamps = [...byWeek.keys()].map(toUtcTimestamp);

    if (range) {
        timestamps.push(mondayOf(range.from), mondayOf(range.to));
    }
    if (!timestamps.length) {
        return [];
    }

    const first = Math.min(...timestamps);
    const last = Math.max(...timestamps);

    const bins: GroupedWeekBin[] = [];

    for (let timestamp = first; timestamp <= last; timestamp += WEEK_MS) {
        const weekStart = toDateString(timestamp);

        bins.push({ weekStart, timestamp, valueById: byWeek.get(weekStart) ?? new Map() });
    }

    return bins;
}

/** Every series in the loaded rows, ranked by its total over the whole fetched period. */
export function rankSeries(rows: GroupedWeeklyRow[]): SeriesRanking[] {
    const totals = new Map<number, { name: string; total: number }>();

    for (const row of rows) {
        const entry = totals.get(row.id) ?? { name: row.name, total: 0 };

        entry.total += row.value;
        totals.set(row.id, entry);
    }

    return [...totals.entries()].map(([id, entry]) => ({ id, ...entry })).sort((a, b) => b.total - a.total);
}

/** One series per ranked, checked id, each zero-filled across every bin. */
function buildSeries(bins: GroupedWeekBin[], ranking: SeriesRanking[], selectedIds: Set<number>): GroupedWeeklySeries[] {
    return ranking
        .filter((entry) => selectedIds.has(entry.id))
        .map((entry) => ({
            id: entry.id,
            name: entry.name,
            data: bins.map((bin) => ({ x: bin.timestamp, y: bin.valueById.get(entry.id) ?? 0 })),
        }));
}

export type GroupedWeeklyAggregation = {
    ranking: ComputedRef<SeriesRanking[]>;
    selectedIds: ComputedRef<Set<number>>;
    series: ComputedRef<GroupedWeeklySeries[]>;
    toggle: (id: number) => void;
};

/**
 * Everything a grouped weekly chart derives from the loaded weekly rows: the series ranking by
 * total, which of them are checked in the dropdown, and the per-series data the chart draws.
 *
 * `selectionResetKey` identifies the scope the rows were fetched for (the period plus any filters
 * that narrow them); it is compared by identity, so pass a primitive rather than an object rebuilt
 * on every change. Without a `range` the bins run from the first populated week to the last.
 */
export function useGroupedWeeklyAggregation(rows: Ref<GroupedWeeklyRow[]>, selectionResetKey: Ref<unknown>, range: Ref<WeeklyBinRange | null> = ref(null)): GroupedWeeklyAggregation {
    const bins = computed(() => buildWeeklyBins(rows.value, range.value));
    const ranking = computed(() => rankSeries(rows.value));

    /**
     * Null while nothing has been manually toggled since the last change of scope - top 5 applies. Within one
     * scope the ranking can move under a manual selection without touching what is checked.
     */
    const manualSelection = ref<Set<number> | null>(null);

    const selectedIds = computed(() => manualSelection.value ?? new Set(ranking.value.slice(0, TOP_DEFAULT_COUNT).map((entry) => entry.id)));

    // Reset outright, not just re-sorted: a series unchecked in one scope must not stay silently hidden in one not yet looked at.
    watch(selectionResetKey, () => {
        manualSelection.value = null;
    });

    function toggle(id: number) {
        const next = new Set(selectedIds.value);

        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }

        manualSelection.value = next;
    }

    const series = computed(() => buildSeries(bins.value, ranking.value, selectedIds.value));

    return { ranking, selectedIds, series, toggle };
}
